/**
 * PullToRefresh — mobile pull-to-refresh gesture.
 * Wraps children in a scrollable container that supports vertical pull-down gesture.
 */
import { useState, useRef, useCallback } from 'react';

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: React.ReactNode;
}

const THRESHOLD = 80;

export function PullToRefresh({ onRefresh, children }: PullToRefreshProps) {
  const [pullDelta, setPullDelta] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [touchAction, setTouchAction] = useState<'pan-y' | 'none'>('pan-y');
  const startY = useRef(0);
  const activated = useRef(false);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (refreshing) return;
    startY.current = e.touches[0].clientY;
    activated.current = false;
  }, [refreshing]);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (refreshing) return;
    const delta = e.touches[0].clientY - startY.current;
    if (delta > 0) {
      setTouchAction('none');  // Block all touch gestures during pull via CSS
      if (!activated.current && delta >= THRESHOLD) {
        activated.current = true;
      }
      setPullDelta(delta);
    }
  }, [refreshing]);

  const onTouchEnd = useCallback(async () => {
    if (refreshing || !activated.current) {
      setPullDelta(0);
      setTouchAction('pan-y');
      return;
    }
    setRefreshing(true);
    setPullDelta(0);
    setTouchAction('pan-y');
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
      setTouchAction('pan-y');
    }
  }, [refreshing, onRefresh]);

  const progress = Math.min(pullDelta / THRESHOLD, 1);

  return (
    <div
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      className={pullDelta > 0 ? 'overscroll-none' : ''}
      style={{
        touchAction,
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <div
        className="transition-none"
        style={{
          transform: `translateY(${pullDelta * 0.4}px)`,
          pointerEvents: refreshing ? 'none' : undefined,
        }}
      >
        {children}
      </div>
      {progress > 0 && (
        <div
          className="flex items-center justify-center gap-2 py-3 text-sm font-medium"
          style={{
            color: progress >= 1
              ? 'rgb(59 130 246)'
              : `rgba(59, 130, 246, ${progress})`,
          }}
        >
          {refreshing ? (
            <>
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Refreshing...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
              Release to refresh
            </>
          )}
        </div>
      )}
    </div>
  );
}
